import { create } from 'zustand';
import type { CalendarEvent, Calendar } from '../types';
import calendarApiService from '../services/calendarApi.service';
import type { CalendarTeam } from '../services/calendarApi.service';

interface CalDAVCalendar {
  id: string;
  name: string;
  displayName: string;
  color: string;
  url?: string;
  syncEnabled: boolean;
  lastSynced?: Date;
  eventCount?: number;
}

interface InviteResult {
  sent: string[];
  failed: { email: string; error: string }[];
}

interface CalendarStore {
  // State
  events: CalendarEvent[];
  calendars: Calendar[];
  caldavCalendars: CalDAVCalendar[];
  teams: CalendarTeam[];
  selectedTeamId: string | null;
  showTeamEvents: boolean;
  selectedEvent: CalendarEvent | null;
  selectedDate: Date;
  view: 'month' | 'week' | 'day' | 'agenda';
  loading: boolean;
  syncing: boolean;
  sendingInvites: boolean;
  error: string | null;
  
  // Actions
  setEvents: (events: CalendarEvent[]) => void;
  addEvent: (event: CalendarEvent) => void;
  updateEvent: (eventId: string, updates: Partial<CalendarEvent>) => void;
  removeEvent: (eventId: string) => void;
  
  setCalendars: (calendars: Calendar[]) => void;
  setCalDAVCalendars: (calendars: CalDAVCalendar[]) => void;
  setTeams: (teams: CalendarTeam[]) => void;
  setSelectedTeamId: (teamId: string | null) => void;
  setShowTeamEvents: (show: boolean) => void;
  selectEvent: (event: CalendarEvent | null) => void;
  setSelectedDate: (date: Date) => void;
  setView: (view: 'month' | 'week' | 'day' | 'agenda') => void;
  setLoading: (loading: boolean) => void;
  setSyncing: (syncing: boolean) => void;
  setSendingInvites: (sending: boolean) => void;
  setError: (error: string | null) => void;
  
  // API Actions
  fetchEvents: (start?: Date, end?: Date) => Promise<void>;
  fetchCalendars: () => Promise<void>;
  fetchCalDAVCalendars: () => Promise<void>;
  fetchTeams: () => Promise<void>;
  syncCalDAV: (calendarId?: string) => Promise<boolean>;
  createEvent: (event: Omit<CalendarEvent, 'id'> & { team_id?: string; is_team_event?: boolean }) => Promise<CalendarEvent | null>;
  saveEvent: (event: CalendarEvent) => Promise<boolean>;
  deleteEvent: (eventId: string) => Promise<boolean>;
  rsvpToEvent: (eventId: string, response: 'accepted' | 'declined' | 'tentative') => Promise<boolean>;
  
  // Meeting Invite Actions
  createEventWithInvites: (
    event: Omit<CalendarEvent, 'id'>,
    options?: { sendInvites?: boolean; customMessage?: string; calendarUrl?: string }
  ) => Promise<{ event: CalendarEvent | null; inviteResult?: InviteResult }>;
  updateEventWithInvites: (
    event: CalendarEvent,
    options?: { sendInvites?: boolean; customMessage?: string }
  ) => Promise<{ success: boolean; inviteResult?: InviteResult }>;
  deleteEventWithCancellation: (
    event: CalendarEvent,
    options?: { sendCancellation?: boolean; reason?: string }
  ) => Promise<{ success: boolean; cancellationResult?: InviteResult }>;
  sendMeetingInvites: (event: CalendarEvent, customMessage?: string) => Promise<InviteResult>;
  sendRsvpResponse: (
    event: CalendarEvent,
    response: 'accepted' | 'declined' | 'tentative',
    comment?: string
  ) => Promise<boolean>;
  
  // Reset
  reset: () => void;
}

const initialState = {
  events: [] as CalendarEvent[],
  calendars: [
    { id: 'default', name: 'My Calendar', color: '#3b82f6', visible: true },
  ] as Calendar[],
  caldavCalendars: [] as CalDAVCalendar[],
  teams: [] as CalendarTeam[],
  selectedTeamId: null as string | null,
  showTeamEvents: true,
  selectedEvent: null as CalendarEvent | null,
  selectedDate: new Date(),
  view: 'month' as const,
  loading: false,
  syncing: false,
  sendingInvites: false,
  error: null as string | null,
};

export const useCalendarStore = create<CalendarStore>((set, get) => ({
  ...initialState,
  
  // Basic setters
  setEvents: (events: CalendarEvent[]) => set({ events }),
  
  addEvent: (event: CalendarEvent) => set((state) => ({
    events: [...state.events, event]
  })),
  
  updateEvent: (eventId: string, updates: Partial<CalendarEvent>) => set((state) => ({
    events: state.events.map((event) =>
      event.id === eventId ? { ...event, ...updates } : event
    ),
    selectedEvent: state.selectedEvent?.id === eventId
      ? { ...state.selectedEvent, ...updates }
      : state.selectedEvent
  })),
  
  removeEvent: (eventId: string) => set((state) => ({
    events: state.events.filter((event) => event.id !== eventId),
    selectedEvent: state.selectedEvent?.id === eventId ? null : state.selectedEvent
  })),
  
  setCalendars: (calendars: Calendar[]) => set({ calendars }),
  setCalDAVCalendars: (calendars: CalDAVCalendar[]) => set({ caldavCalendars: calendars }),
  setTeams: (teams: CalendarTeam[]) => set({ teams }),
  setSelectedTeamId: (teamId: string | null) => set({ selectedTeamId: teamId }),
  setShowTeamEvents: (show: boolean) => set({ showTeamEvents: show }),
  selectEvent: (event: CalendarEvent | null) => set({ selectedEvent: event }),
  setSelectedDate: (date: Date) => set({ selectedDate: date }),
  setView: (view: 'month' | 'week' | 'day' | 'agenda') => set({ view }),
  setLoading: (loading: boolean) => set({ loading }),
  setSyncing: (syncing: boolean) => set({ syncing }),
  setSendingInvites: (sending: boolean) => set({ sendingInvites: sending }),
  setError: (error: string | null) => set({ error }),
  
  // API Actions
  fetchEvents: async (start?: Date, end?: Date) => {
    const { selectedTeamId, showTeamEvents } = get();
    set({ loading: true, error: null });
    
    try {
      const response = await calendarApiService.getEvents(start, end, {
        teamId: selectedTeamId || undefined,
        includeTeamEvents: showTeamEvents,
      });
      
      if (response.success && response.data) {
        // Normalize events - map backend field names to frontend
        const events = response.data.map((event: any) => ({
          id: event.id,
          title: event.title,
          description: event.description,
          location: event.location,
          // Map backend start_time/end_time to frontend start/end
          start: new Date(event.start_time || event.start),
          end: new Date(event.end_time || event.end),
          allDay: event.all_day ?? event.allDay,
          color: event.color,
          calendarId: event.calendar_name || event.calendarId,
          attendees: typeof event.attendees === 'string' ? JSON.parse(event.attendees) : event.attendees,
          reminders: typeof event.reminders === 'string' ? JSON.parse(event.reminders) : event.reminders,
          recurrence: event.recurrence_rule || event.recurrence,
          status: event.status,
          myStatus: event.user_response || event.myStatus,
          organizer: typeof event.organizer === 'string' ? JSON.parse(event.organizer) : event.organizer,
          isOrganizer: event.is_organizer,
          visibility: event.visibility,
          icsContent: event.ics_content,
          eventType: event.event_type,
          meetingUrl: event.meeting_url,
          isVideoMeeting: event.event_type === 'online' || !!event.meeting_url,
          syncSource: event.sync_source,
          caldavUid: event.caldav_uid,
          teamId: event.team_id,
          teamName: event.team_name,
          teamColor: event.team_color,
          isTeamEvent: event.is_team_event,
        }));
        
        set({ events, loading: false });
      } else {
        set({ error: response.error || 'Failed to fetch events', loading: false });
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      set({ error: 'Failed to fetch events', loading: false });
    }
  },
  
  fetchCalendars: async () => {
    // For now, calendars are managed locally
    // This can be expanded to fetch from API later
  },

  fetchTeams: async () => {
    try {
      const response = await calendarApiService.getTeams();
      if (response.success && response.data) {
        set({ teams: response.data });
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  },

  fetchCalDAVCalendars: async () => {
    try {
      // Get auth token
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {
            console.error('Failed to parse auth_tokens:', e);
          }
        }
      }

      if (!token) return;

      const response = await fetch('/api/v1/caldav', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getCalendars' })
      });

      const result = await response.json();
      if (result.success && result.calendars) {
        set({ caldavCalendars: result.calendars });
      }
    } catch (error) {
      console.error('Error fetching CalDAV calendars:', error);
    }
  },

  syncCalDAV: async (calendarId?: string) => {
    set({ syncing: true, error: null });
    
    try {
      // Get auth token
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {
            console.error('Failed to parse auth_tokens:', e);
          }
        }
      }

      if (!token) {
        set({ syncing: false, error: 'Not authenticated' });
        return false;
      }

      // First, get the list of calendars to sync
      const calendarsResponse = await fetch('/api/v1/caldav', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'getCalendars' })
      });

      const calendarsResult = await calendarsResponse.json();
      
      if (!calendarsResult.success || !calendarsResult.calendars?.length) {
        set({ syncing: false, error: calendarsResult.error || 'No calendars found' });
        return false;
      }

      // Sync each calendar (or just the specified one)
      let syncedAny = false;
      for (const calendar of calendarsResult.calendars) {
        if (calendarId && calendar.id !== calendarId) continue;
        
        const response = await fetch('/api/v1/caldav', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            action: 'syncCalendar',
            calendarUrl: calendar.url,
            calendarName: calendar.displayName || calendar.id
          })
        });

        const result = await response.json();
        if (result.success) {
          syncedAny = true;
          console.log(`[CalDAV] Synced ${calendar.displayName}: +${result.added} ~${result.updated} -${result.deleted}`);
        }
      }
      
      if (syncedAny) {
        // Refresh events after sync
        const state = get();
        await state.fetchEvents();
        set({ syncing: false });
        return true;
      } else {
        set({ syncing: false, error: 'No calendars synced' });
        return false;
      }
    } catch (error: any) {
      console.error('Error syncing CalDAV:', error);
      set({ syncing: false, error: error.message || 'Sync failed' });
      return false;
    }
  },
  
  createEvent: async (eventData: Omit<CalendarEvent, 'id'> & { team_id?: string; is_team_event?: boolean }) => {
    set({ loading: true, error: null });
    
    try {
      const response = await calendarApiService.createEvent(eventData as CalendarEvent & { team_id?: string; is_team_event?: boolean });
      
      if (response.success && response.data) {
        const data = response.data as any;
        // Map backend field names to frontend
        const newEvent = {
          id: data.id,
          title: data.title,
          description: data.description,
          location: data.location,
          start: new Date(data.start_time || data.start),
          end: new Date(data.end_time || data.end),
          allDay: data.all_day ?? data.allDay,
          color: data.color,
          calendarId: data.calendar_name || data.calendarId,
          attendees: typeof data.attendees === 'string' ? JSON.parse(data.attendees) : data.attendees,
          reminders: typeof data.reminders === 'string' ? JSON.parse(data.reminders) : data.reminders,
          recurrence: data.recurrence_rule || data.recurrence,
          status: data.status,
          myStatus: data.user_response || data.myStatus,
          eventType: data.event_type,
          meetingUrl: data.meeting_url,
          teamId: data.team_id,
          isTeamEvent: data.is_team_event,
        };
        
        get().addEvent(newEvent);
        set({ loading: false });
        return newEvent;
      } else {
        set({ error: response.error || 'Failed to create event', loading: false });
        return null;
      }
    } catch (error) {
      console.error('Error creating event:', error);
      set({ error: 'Failed to create event', loading: false });
      return null;
    }
  },
  
  saveEvent: async (event: CalendarEvent) => {
    set({ loading: true, error: null });
    
    try {
      const response = await calendarApiService.updateEvent(event.id, event);
      
      if (response.success) {
        get().updateEvent(event.id, event);
        set({ loading: false });
        return true;
      } else {
        set({ error: response.error || 'Failed to save event', loading: false });
        return false;
      }
    } catch (error) {
      console.error('Error saving event:', error);
      set({ error: 'Failed to save event', loading: false });
      return false;
    }
  },
  
  deleteEvent: async (eventId: string) => {
    try {
      const response = await calendarApiService.deleteEvent(eventId);
      
      if (response.success) {
        get().removeEvent(eventId);
        return true;
      } else {
        set({ error: response.error || 'Failed to delete event' });
        return false;
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      set({ error: 'Failed to delete event' });
      return false;
    }
  },
  
  rsvpToEvent: async (eventId: string, response: 'accepted' | 'declined' | 'tentative') => {
    try {
      const apiResponse = await calendarApiService.rsvpToEvent(eventId, response);
      
      if (apiResponse.success) {
        get().updateEvent(eventId, { myStatus: response });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating RSVP:', error);
      return false;
    }
  },

  // =========================================================================
  // MEETING INVITE METHODS (iMIP)
  // =========================================================================

  createEventWithInvites: async (eventData, options = {}) => {
    set({ loading: true, sendingInvites: !!options.sendInvites, error: null });

    try {
      // Get auth token
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {
            console.error('Failed to parse auth_tokens:', e);
          }
        }
      }

      if (!token) {
        set({ loading: false, sendingInvites: false, error: 'Not authenticated' });
        return { event: null };
      }

      // Get CalDAV calendar URL if not provided
      let calendarUrl = options.calendarUrl;
      if (!calendarUrl && options.sendInvites) {
        const state = get();
        const primaryCalendar = state.caldavCalendars.find(c => c.url) || state.caldavCalendars[0];
        calendarUrl = primaryCalendar?.url;
      }

      // If we have a CalDAV calendar and want to send invites, use the caldav API
      if (calendarUrl && options.sendInvites) {
        const response = await fetch('/api/v1/caldav', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            action: 'createEventWithInvites',
            calendarUrl,
            event: {
              summary: eventData.title,
              description: eventData.description,
              location: eventData.location,
              dtstart: eventData.start,
              dtend: eventData.end,
              allDay: eventData.allDay,
              attendees: eventData.attendees?.map(a => ({
                email: a.email || a,
                name: a.name,
                role: 'REQ-PARTICIPANT',
                partstat: 'NEEDS-ACTION',
                rsvp: true
              })),
              url: eventData.meetingUrl
            },
            sendInvites: true,
            customMessage: options.customMessage
          })
        });

        const result = await response.json();

        if (result.success) {
          // Also create in local DB via calendar API
          const localResponse = await calendarApiService.createEvent(eventData as CalendarEvent);
          
          const newEvent = localResponse.success && localResponse.data ? {
            id: (localResponse.data as any).id,
            title: eventData.title || '',
            description: eventData.description,
            location: eventData.location,
            start: new Date(eventData.start as any),
            end: new Date(eventData.end as any),
            allDay: eventData.allDay,
            color: eventData.color,
            calendarId: eventData.calendarId,
            attendees: eventData.attendees,
            caldavUid: result.uid
          } : null;

          if (newEvent) {
            get().addEvent(newEvent as CalendarEvent);
          }

          set({ loading: false, sendingInvites: false });
          return {
            event: newEvent as CalendarEvent | null,
            inviteResult: {
              sent: result.invitesSent || [],
              failed: result.invitesFailed || []
            }
          };
        } else {
          set({ loading: false, sendingInvites: false, error: result.error });
          return { event: null };
        }
      }

      // Fallback to regular event creation without CalDAV
      const response = await calendarApiService.createEvent(eventData as CalendarEvent);
      
      if (response.success && response.data) {
        const data = response.data as any;
        const newEvent = {
          id: data.id,
          title: data.title,
          description: data.description,
          location: data.location,
          start: new Date(data.start_time || data.start),
          end: new Date(data.end_time || data.end),
          allDay: data.all_day ?? data.allDay,
          color: data.color,
          calendarId: data.calendar_name || data.calendarId,
          attendees: typeof data.attendees === 'string' ? JSON.parse(data.attendees) : data.attendees,
          meetingUrl: data.meeting_url || eventData.meetingUrl,
        };
        
        get().addEvent(newEvent as CalendarEvent);
        
        // Send email invites via regular email API if requested (fallback when CalDAV not configured)
        let inviteResult: InviteResult | undefined;
        if (options.sendInvites && eventData.attendees && eventData.attendees.length > 0) {
          try {
            const emailResponse = await fetch('/api/v1/calendar/send-invites', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                event: {
                  id: newEvent.id,
                  title: newEvent.title,
                  description: newEvent.description,
                  location: newEvent.location,
                  start: newEvent.start,
                  end: newEvent.end,
                  allDay: newEvent.allDay,
                  meetingUrl: newEvent.meetingUrl,
                  attendees: newEvent.attendees
                },
                customMessage: options.customMessage
              })
            });
            
            const emailResult = await emailResponse.json();
            if (emailResult.success) {
              inviteResult = {
                sent: emailResult.sent || [],
                failed: emailResult.failed || []
              };
            } else {
              inviteResult = {
                sent: [],
                failed: (eventData.attendees || []).map(a => ({
                  email: typeof a === 'string' ? a : (a.email || ''),
                  error: emailResult.error || 'Failed to send invites'
                }))
              };
            }
          } catch (err) {
            console.error('Failed to send email invites:', err);
            inviteResult = {
              sent: [],
              failed: (eventData.attendees || []).map(a => ({
                email: typeof a === 'string' ? a : (a.email || ''),
                error: err instanceof Error ? err.message : 'Failed to send invites'
              }))
            };
          }
        }
        
        set({ loading: false, sendingInvites: false });
        return { event: newEvent as CalendarEvent, inviteResult };
      } else {
        set({ loading: false, sendingInvites: false, error: response.error || 'Failed to create event' });
        return { event: null };
      }
    } catch (error) {
      console.error('Error creating event with invites:', error);
      set({ loading: false, sendingInvites: false, error: error instanceof Error ? error.message : 'Failed to create event' });
      return { event: null };
    }
  },

  updateEventWithInvites: async (event, options = {}) => {
    set({ loading: true, sendingInvites: !!options.sendInvites, error: null });

    try {
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {}
        }
      }

      if (!token) {
        set({ loading: false, sendingInvites: false, error: 'Not authenticated' });
        return { success: false };
      }

      // If event has CalDAV UID and we want to send invites
      if (event.caldavUid && options.sendInvites) {
        const state = get();
        const primaryCalendar = state.caldavCalendars.find(c => c.url) || state.caldavCalendars[0];
        const calendarUrl = primaryCalendar?.url;

        if (calendarUrl) {
          const response = await fetch('/api/v1/caldav', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'updateEventWithInvites',
              calendarUrl,
              event: {
                uid: event.caldavUid,
                summary: event.title,
                description: event.description,
                location: event.location,
                dtstart: event.start,
                dtend: event.end,
                allDay: event.allDay,
                attendees: event.attendees?.map(a => ({
                  email: a.email || a,
                  name: a.name,
                  role: 'REQ-PARTICIPANT',
                  partstat: a.status?.toUpperCase() || 'NEEDS-ACTION',
                  rsvp: true
                })),
                url: event.meetingUrl
              },
              sendInvites: true,
              customMessage: options.customMessage
            })
          });

          const result = await response.json();

          if (result.success) {
            // Update local DB
            await calendarApiService.updateEvent(event.id, event);
            get().updateEvent(event.id, event);
            set({ loading: false, sendingInvites: false });
            return {
              success: true,
              inviteResult: {
                sent: result.invitesSent || [],
                failed: result.invitesFailed || []
              }
            };
          }
        }
      }

      // Fallback to regular update
      const response = await calendarApiService.updateEvent(event.id, event);
      
      if (response.success) {
        get().updateEvent(event.id, event);
        set({ loading: false, sendingInvites: false });
        return { success: true };
      } else {
        set({ loading: false, sendingInvites: false, error: response.error || 'Failed to update event' });
        return { success: false };
      }
    } catch (error) {
      console.error('Error updating event with invites:', error);
      set({ loading: false, sendingInvites: false, error: error instanceof Error ? error.message : 'Failed to update event' });
      return { success: false };
    }
  },

  deleteEventWithCancellation: async (event, options = {}) => {
    set({ loading: true, error: null });

    try {
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {}
        }
      }

      if (!token) {
        set({ loading: false, error: 'Not authenticated' });
        return { success: false };
      }

      // If event has CalDAV UID and we want to send cancellation
      if (event.caldavUid && options.sendCancellation && event.attendees?.length) {
        const state = get();
        const primaryCalendar = state.caldavCalendars.find(c => c.url) || state.caldavCalendars[0];
        const calendarUrl = primaryCalendar?.url;

        if (calendarUrl) {
          const response = await fetch('/api/v1/caldav', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'deleteEventWithCancellation',
              calendarUrl,
              event: {
                uid: event.caldavUid,
                summary: event.title,
                dtstart: event.start,
                dtend: event.end,
                allDay: event.allDay,
                attendees: event.attendees?.map(a => ({
                  email: a.email || a,
                  name: a.name
                }))
              },
              sendCancellation: true,
              reason: options.reason
            })
          });

          const result = await response.json();

          // Also delete from local DB
          await calendarApiService.deleteEvent(event.id);
          get().removeEvent(event.id);

          set({ loading: false });
          return {
            success: result.success,
            cancellationResult: {
              sent: result.cancellationsSent || [],
              failed: result.cancellationsFailed || []
            }
          };
        }
      }

      // Regular delete
      const response = await calendarApiService.deleteEvent(event.id);
      
      if (response.success) {
        get().removeEvent(event.id);
        set({ loading: false });
        return { success: true };
      } else {
        set({ loading: false, error: response.error || 'Failed to delete event' });
        return { success: false };
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      set({ loading: false, error: error instanceof Error ? error.message : 'Failed to delete event' });
      return { success: false };
    }
  },

  sendMeetingInvites: async (event, customMessage) => {
    set({ sendingInvites: true, error: null });

    try {
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {}
        }
      }

      if (!token || !event.caldavUid) {
        set({ sendingInvites: false, error: 'Not authenticated or event not synced' });
        return { sent: [], failed: [] };
      }

      const response = await fetch('/api/v1/caldav', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'sendInvites',
          event: {
            uid: event.caldavUid,
            summary: event.title,
            description: event.description,
            location: event.location,
            dtstart: event.start,
            dtend: event.end,
            allDay: event.allDay,
            attendees: event.attendees?.map(a => ({
              email: a.email || a,
              name: a.name,
              role: 'REQ-PARTICIPANT',
              partstat: 'NEEDS-ACTION',
              rsvp: true
            })),
            url: event.meetingUrl
          },
          customMessage
        })
      });

      const result = await response.json();
      set({ sendingInvites: false });

      if (result.success) {
        return { sent: result.sent || [], failed: result.failed || [] };
      } else {
        set({ error: result.error });
        return { sent: [], failed: [] };
      }
    } catch (error) {
      console.error('Error sending invites:', error);
      set({ sendingInvites: false, error: error instanceof Error ? error.message : 'Failed to send invites' });
      return { sent: [], failed: [] };
    }
  },

  sendRsvpResponse: async (event, response, comment) => {
    try {
      let token: string | null = null;
      if (typeof window !== 'undefined') {
        const storedTokens = localStorage.getItem('auth_tokens');
        if (storedTokens) {
          try {
            const parsed = JSON.parse(storedTokens);
            token = parsed.access_token || null;
          } catch (e) {}
        }
      }

      if (!token || !event.caldavUid || !event.organizer) {
        return false;
      }

      const apiResponse = await fetch('/api/v1/caldav', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'sendRsvp',
          event: {
            uid: event.caldavUid,
            summary: event.title,
            dtstart: event.start,
            dtend: event.end,
            allDay: event.allDay,
            localId: event.id
          },
          response: response.toUpperCase(),
          organizer: {
            email: event.organizer.email || event.organizer,
            name: event.organizer.name
          },
          comment
        })
      });

      const result = await apiResponse.json();

      if (result.success) {
        get().updateEvent(event.id, { myStatus: response });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error sending RSVP:', error);
      return false;
    }
  },
  
  reset: () => set(initialState),
}));
